import { variantTypes } from '../variants/Variants.constants';

export const reportSortColumns = [
  'drafted',
  'finalised',
] as const;

export const reportTypes = ['MOLECULAR_REPORT', 'MTB_REPORT', 'GERMLINE_REPORT', 'PRECLINICAL_REPORT'] as const;
export const reportPseudoStatuses = ['On Hold', 'N/A'] as const;

// Statuses a report itself can hold, in `zcc_reports.status`. Deliberately separate
// from `approvalStatuses`, which tracks a single approver's sign-off on a report:
// the two lists overlap heavily but diverge on the sign-off step, where a report is
// `finalised` while an individual approval is `approved`.
export const reportStatuses = [
  'pending',
  'finalised',
  'cancelled',
  'rejected',
  'ready_to_send',
  'sent',
  'done',
  // A short-lived claim held while a report is being emailed, so that two replicas
  // of the cron cannot both pick up the same report.
  'sending',
  // Any step of the outbound pipeline failed — emailing the report, or filing it into
  // Labmatrix afterwards. Which one, and why, is recorded on the audit log.
  'failed',
] as const;

// Short name per report type, used in outbound email subjects and bodies.
export const reportTypeLabels = {
  MOLECULAR_REPORT: 'Molecular',
  MTB_REPORT: 'MTB',
  GERMLINE_REPORT: 'Germline',
  PRECLINICAL_REPORT: 'Preclinical',
} as const satisfies Record<typeof reportTypes[number], string>;

// Name embedded in the downloaded/emailed/Labmatrix-filed PDF filename. Distinct
// from the labels above — these are the long-form names clinicians see on the file.
export const reportDownloadNames = {
  MOLECULAR_REPORT: 'Molecular Report',
  MTB_REPORT: 'MTB Report',
  GERMLINE_REPORT: 'Germline Cancer Genetics Report',
  PRECLINICAL_REPORT: 'MTB Preclinical Report',
} as const satisfies Record<typeof reportTypes[number], string>;

// Every action that can be written to zcc_report_audit_logs. Keep this in sync with
// the label map in the frontend's ReportAuditLogModal — the map is typed against this
// list, so an entry missing on either side is a compile error rather than an
// unlabelled chip in the UI.
export const reportAuditActions = [
  'created',
  'cancelled',
  'finalised',
  'file_replaced',
  'ready_to_send',
  'cancel_ready_to_send',
  'reopened',
  'sent',
  'resent',
  'done',
  'failed',
] as const;

// Non-variant amendment sections — variant-specific sections use variantTypes keys directly
export const nonVariantAmendmentSections = [
  'patient-profile',
  'tumour-molecular-profile',
  'tumour-immune-profile',
  'molecular-classifiers',
  'molecular-summary',
  'additional-notes',
  'recommendations',
] as const;

export const REPORT_AMENDMENT_META_KEY = 'report.field.annotations' as const;
export const REPORT_NO_COPY_METADATA_KEYS = [
  'report.liquidfiles.messageId',
  'report.labmatrix.attachmentId',
] as const;
export const reportMetadataKeys = [
  'molecular.hidePanel',
  'preclinical.htsBiosampleId',
  'preclinical.htsScreen',
  'germline.attachments',
  'germline.forceApproval',
  'report.field.annotations',
  ...REPORT_NO_COPY_METADATA_KEYS,
  ...nonVariantAmendmentSections.map((s) => `report.amendment.${s}` as const),
  ...variantTypes.map((v) => `report.amendment.${v}` as const),
  ...variantTypes.map((v) => `report.no.result.message.${v}` as const),
  ...variantTypes.map((v) => `report.result.order.${v}` as const),
] as const;
